/**
 * Sidebar Component
 * 2depth 메뉴 구조를 지원하는 사이드바 컴포넌트
 * 메뉴 펼침/접기 기능 포함
 * RBAC 권한 기반 메뉴 필터링 적용
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Bell, Briefcase, Users, User, FolderTree, Package, Network,
  ChevronDown, ChevronRight, Shield, ClipboardList, FileText, Trophy
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuthContext';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { useAuthorization } from '../hooks/useAuthorization';
import { canAccessRoute, hasRole } from '../../utils/rbac'; // V148.1: hasRole 추가
import { useUnreadAnnouncementCount } from '../hooks/useAnnouncements'; // V84: 새 공지 알림
import { useOrgManagementPolicy } from '../hooks/useOrgManagementPolicy'; // V136: 직책 레벨 정책
import { useIsOrgApprover } from '../hooks/useIsOrgApprover'; // V168: 조직관리 승인자 확인
import { useWorkforceOverviewAccess } from '../hooks/useWorkforceOverviewAccess'; // V70.27: 인력 현황 접근 확인
import { useMyTournamentAccess } from '../hooks/useMyTournamentAccess'; // V70 Tournament: 공개 ON + 대상자 매칭 시만
import { ADMIN_LIKE_ROLES } from '../../types/role'; // V29.2: 관리자급 역할 통합
import { JOB_TITLE_LEVEL_ORDER, JobTitleLevel } from '../types/orgManagementPolicy'; // V60: TL 판별
import { ROULETTE_PATH, canHostRoulette } from '../utils/rouletteAccess'; // V70G8G: 엔글 룰렛 진행자 판정 (페이지와 동일)
// V70-S: 감사 대응 인사관리 메뉴 판정 (라우트 가드와 동일 기준)
import {
  HR_COMPANY_WORK_STATUS_PATH,
  HR_OVERTIME_MANAGEMENT_PATH,
  HR_KR_AUDIT_PATHS,
  canAccessHrKrAudit,
} from '../utils/hrKrAuditAccess';

/**
 * V59: 조직관리 메뉴 노출 허용 역할
 * V60: MM관리는 별도 처리 (TL은 등록만 허용)
 * - role_hr 단독으로는 노출 금지
 * - role_hr + role_manager 조합은 허용 (역할 중 하나라도 허용이면 노출)
 */
const ORG_MANAGEMENT_ALLOWED_ROLES = [
  'system_admin',
  'role_admin_kr',
  'role_admin_cn',
  'role_manager',
  'role_exec',
] as const;

/**
 * V60: MM관리 전체 접근 허용 역할 (현황 + 등록)
 * TL은 등록만 허용되므로 별도 처리
 */
const MM_FULL_ACCESS_ROLES = [
  'system_admin',
  'role_admin_kr',
  'role_admin_cn',
  'role_manager',
  'role_exec',
] as const;

/**
 * V114.4: role_tl 역할 ID
 * auth_roleAssignments에서 role_tl 역할이 부여된 직원도 TL로 인정
 */
const ROLE_TL = 'role_tl';

/**
 * V60: 직책 레벨이 TEAM_LEADER 이상인지 확인
 * TEAM_LEADER(4), DIRECTOR(3), HQ_HEAD(2), CEO(1) → true
 * SPECIAL(5), MEMBER(6) → false
 */
function isTeamLeaderOrAbove(level: JobTitleLevel | undefined): boolean {
  if (!level) return false;
  const order = JOB_TITLE_LEVEL_ORDER[level];
  return order <= 4;
}

// 하위 메뉴 아이템 인터페이스
interface SubMenuItem {
  id: string;
  label: string;
  path: string;
}

// 메뉴 아이템 인터페이스
interface MenuItem {
  id: string;
  label: string;
  path?: string; // 단일 메뉴인 경우
  icon: React.ComponentType<any>;
  subMenus?: SubMenuItem[]; // 하위 메뉴가 있는 경우
}

/**
 * 전체 메뉴 구조 정의
 */
const menuStructure: MenuItem[] = [
  // V55.23: 대시보드 메뉴 제거 (공지사항이 기본 첫 화면으로 변경됨)
  {
    id: 'announcements',
    label: '공지사항',
    path: '/announcements',
    icon: Bell,
  },
  {
    id: 'organization',
    label: '조직',
    icon: Network,
    subMenus: [
      { id: 'org-chart', label: '조직도', path: '/organization' },
      { id: 'seat-map', label: '좌석 배치도', path: '/organization/seat-map' }, // V52
    ],
  },
  {
    id: 'work',
    label: '근로 관리',
    icon: Briefcase,
    subMenus: [
      { id: 'work-my-dashboard', label: '개인 대시보드', path: '/work/my-dashboard' },
      { id: 'work-schedule', label: '근로 일정', path: '/work/schedule' },
      { id: 'my-leave', label: '내 휴가 관리', path: '/my/leave' },
      { id: 'work-leave-calendar', label: '조직 휴가 캘린더', path: '/work/leave-calendar' }, // V66: 근로관리 하위로 이전
      { id: 'work-my-approvals', label: '내 승인 요청', path: '/work/my-approvals' },
      { id: 'work-approvals', label: '승인 요청 관리', path: '/work/approvals' },
    ],
  },
  // V55.35: 내 인사기록 메뉴
  {
    id: 'my-hr',
    label: '내 인사 기록',
    icon: ClipboardList,
    subMenus: [
      { id: 'my-interviews', label: '면담 기록', path: '/my/interviews' },
      { id: 'my-evaluations', label: '평가 기록', path: '/my/evaluations' },
    ],
  },
  // V250: 증명서 발급
  {
    id: 'my-certificates',
    label: '증명서 발급',
    path: '/certificates',
    icon: FileText,
  },
  {
    id: 'hr',
    label: '인사 관리',
    icon: Users,
    subMenus: [
      { id: 'hr-dashboard', label: '인사팀 대시보드', path: '/hr/dashboard' },
      { id: 'hr-employees', label: '직원 관리', path: '/hr/employees' },
      { id: 'hr-employment-status', label: '재직 현황', path: '/hr/employment-status' },
      { id: 'hr-employment-management', label: '고용 관리', path: '/hr/employment-management' },
      { id: 'hr-org-units', label: '조직 관리', path: '/hr/org-units' },
      { id: 'hr-positions', label: '직위 관리', path: '/hr/positions' },
      { id: 'hr-job-titles', label: '직책 관리', path: '/hr/job-titles' },
      { id: 'hr-personnel-appointment', label: '인사 발령', path: '/hr/personnel-appointment' },
      { id: 'hr-terminated-employees', label: '퇴사자 관리', path: '/hr/terminated-employees' }, // V77
      { id: 'hr-transfers', label: '전배 관리', path: '/hr/transfers' },
      { id: 'hr-leave-settings', label: '휴가 설정', path: '/hr/leave-settings' },
      { id: 'hr-leave-overview', label: '휴가 현황', path: '/hr/leave-overview' },
      { id: 'hr-leave-approvals', label: '승인 관리', path: '/hr/leave-approvals' }, // V66: 휴가 승인 관리 → 승인 관리 (휴가 + 휴직 통합)
      // V70-S: 감사 대응 (KR 전사원) — system_admin·role_admin_kr·KR role_hr 전용 (canAccessHrKrAudit)
      { id: 'hr-company-work-status', label: '전사 근로 현황', path: HR_COMPANY_WORK_STATUS_PATH },
      { id: 'hr-overtime-management', label: '연장 근무 관리', path: HR_OVERTIME_MANAGEMENT_PATH },
      { id: 'hr-holidays', label: '공휴일 관리', path: '/hr/holidays' },
      { id: 'hr-performance', label: '인사평가 현황', path: '/hr/performance' },
      { id: 'hr-eval-templates', label: '평가서 관리', path: '/hr/evaluation-templates' }, // V57.2
      { id: 'hr-certificate-management', label: '증명서 관리', path: '/hr/certificate-management' }, // V250
    ],
  },
  {
    id: 'org-management',
    label: '조직 관리',
    icon: FolderTree,
    subMenus: [
      { id: 'org-members', label: '조직장 대시보드', path: '/org-management/members' },
      // V135: 조직장관리 메뉴 삭제
      { id: 'org-projects', label: '프로젝트 관리', path: '/org-management/projects' },
      // V91.3: 태그 마스터 메뉴 제거 (프로젝트 관리 내부 탭으로 이동)
      { id: 'org-work-status', label: '조직 근로 현황', path: '/org-management/work-status' },
      { id: 'org-leave', label: '조직 휴가 현황', path: '/org-management/leave' }, // V5.18: 플레이스홀더 → 실제 페이지로 대체
      // V66: 조직 휴가 캘린더는 근로관리 하위로 이전됨 (전 직원 접근)
      { id: 'org-interviews', label: '조직원 면담', path: '/org-management/interviews' }, // V136, V55.26: 면담기록 → 조직원면담
      { id: 'org-evaluations', label: '조직원 평가', path: '/org-management/evaluations' }, // V57: 평가
      { id: 'org-transfer', label: '조직원 전배', path: '/org-management/transfer' }, // V60: 전배
      { id: 'org-workforce', label: '인력 관리', path: '/org-management/workforce' }, // V68: 인력투입 현황 + 유휴인력 관리
      // V70.27: 인력 현황 — Admin 또는 `Admin > 인력 현황 관리` 등록 사용자만 노출
      { id: 'org-workforce-overview', label: '인력 현황', path: '/org-management/workforce-overview' },
    ],
  },
  {
    id: 'mm',
    label: 'MM 관리',
    icon: Package,
    subMenus: [
      { id: 'mm-overview', label: 'MM 현황', path: '/mm/overview' },
      { id: 'mm-register', label: 'MM 등록', path: '/mm/register' },
      { id: 'mm-master', label: 'MM 마스터', path: '/mm/master' }, // V113
    ],
  },
  // V70 Tournament: 2026년 게임대회 (사용자 메뉴)
  // 공개 ON + 대상자 매칭 사용자에게만 노출 — Sidebar 렌더링 시 useMyTournamentAccess 로 조건부 필터
  // 관리자라는 이유만으로 자동 노출되지 않음 (사용자 지시 §1)
  {
    id: 'tournament',
    label: '2026년 게임대회',
    icon: Trophy,
    subMenus: [
      { id: 'tournament-dashboard', label: '대시보드', path: '/tournament/dashboard' },
      { id: 'tournament-apply', label: '참가신청', path: '/tournament/apply' },
      { id: 'tournament-games', label: '게임센터', path: '/tournament/games' },
      // V70 엔글 룰렛 — 다중 경품 추첨 플랫폼 (서바이벌 / 잭팟 / 룰렛 / 미스터리박스)
      //   V1/V2/V3 마블 레이스는 사용자 결정으로 폐기. 신규 추첨 플랫폼으로 교체.
      { id: 'tournament-roulette', label: '엔글 룰렛', path: '/tournament/roulette' },
    ],
  },
];

/**
 * Admin 전용 메뉴 (userType === 'admin'만 노출)
 */
const adminMenuStructure: MenuItem[] = [
  {
    id: 'admin',
    label: 'Admin',
    icon: Shield,
    subMenus: [
      { id: 'admin-roles', label: '역할 관리', path: '/admin/roles' },
      // V47.15: 테스트도구 (system_admin 전용) - 조건부 필터링은 렌더링 시점에 처리
      { id: 'admin-test-utilities', label: '테스트 도구', path: '/admin/test-utilities' },
      // V82.7 Phase 1: 화이트리스트 (system_admin 전용)
      { id: 'admin-ip-whitelist', label: '화이트리스트', path: '/admin/ip-whitelist' },
      // V67: 알림 마스터 (system_admin 전용)
      { id: 'admin-notifications', label: '알림 마스터', path: '/admin/notifications' },
      // V70.27: 인력 현황 관리 (노출 조직 + 접근 사용자 설정) — ROUTE_PERMISSIONS 'system.admin'
      { id: 'admin-workforce-overview', label: '인력 현황 관리', path: '/admin/workforce-overview' },
      // V70 Tournament: 게임대회 설정 (ROUTE_PERMISSIONS 'system.admin')
      { id: 'admin-tournament-settings', label: '게임대회 설정', path: '/admin/tournament-settings' },
      // V70 Phase A: 참가신청 양식관리 (ROUTE_PERMISSIONS 'system.admin')
      { id: 'admin-tournament-applications', label: '이벤트 신청자 양식관리', path: '/admin/tournament-applications' },
    ],
  },
];

/**
 * Sidebar 컴포넌트
 */
export function Sidebar() {
  const location = useLocation();
  const authContext = useAuthContext();
  const { userType } = useOnboardingStatus();
  const { user } = useAuthorization();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  // V84: 읽지 않은 공지사항 개수 조회
  const { unreadCount: unreadAnnouncementCount } = useUnreadAnnouncementCount();

  // V136: 조직관리 메뉴 직책 레벨 정책
  // V60: userJobTitleLevel 추가 (TL 판별용)
  const { canAccessPath: canAccessByPolicy, userJobTitleLevel } = useOrgManagementPolicy();

  // V168: 조직관리에서 승인자로 지정된 사용자인지 확인
  const { isOrgApprover } = useIsOrgApprover();

  // V70.27: 인력 현황 접근 가능 여부 (서버 SSoT = workforceOverviewSettings.allowedUserIds + Admin)
  // 역할 기반 RBAC 로 표현할 수 없는 "명시 등록" 권한이므로 useIsOrgApprover 와 동일하게 서버 조회.
  const { canAccess: canAccessWorkforceOverview } = useWorkforceOverviewAccess();

  // V70 Tournament: 공개 ON + 대상자 매칭 시만 사용자 메뉴 노출
  // 관리자라는 이유만으로 자동 노출되지 않음 (사용자 지시 §1)
  const { accessible: canAccessTournament } = useMyTournamentAccess();

  // V148.1: role_exec 여부 확인 (auth_roleAssignments 기반 SSoT)
  const isRoleExec = hasRole(authContext, 'role_exec');

  // V91: role_hr 여부 확인 (태그 마스터 접근 차단용)
  const isRoleHr = hasRole(authContext, 'role_hr');

  // V47.15: system_admin 여부 확인 (테스트도구 메뉴 전용)
  const isSystemAdmin = hasRole(authContext, 'system_admin');

  // V70G8G: 엔글 룰렛 메뉴는 페이지(RouletteMainPage)와 같은 기준으로만 노출.
  //   hasRole 은 관리자급(role_admin_kr/cn)도 true 로 보므로 사용하지 않는다. 역할 로딩 전에는 false.
  const canHostRouletteMenu = canHostRoulette(user?.roles);

  // V70-S: 전사 근로 현황 / 연장 근무 관리 — system_admin·role_admin_kr·KR 소속 role_hr 만 (hr.* 권한·관리자급 간주 미사용)
  const canAccessHrKrAuditMenu = canAccessHrKrAudit(user?.roles, user?.profile?.companyCode);

  // V60: 조직관리 메뉴 노출 허용 역할 체크
  // - role_hr 단독으로는 노출 금지
  // - role_hr + role_manager 조합은 허용 (역할 중 하나라도 허용이면 노출)
  const hasOrgManagementAccess = ORG_MANAGEMENT_ALLOWED_ROLES.some(roleId =>
    hasRole(authContext, roleId)
  );

  // V60: MM관리 전체 접근 역할 체크 (현황 + 등록 모두 접근)
  const hasMmFullAccess = MM_FULL_ACCESS_ROLES.some(roleId =>
    hasRole(authContext, roleId)
  );

  // V60: TL 직책 여부 확인 (MM 등록만 허용)
  const isTlByJobTitle = isTeamLeaderOrAbove(userJobTitleLevel);

  // V114.4: role_tl 역할 보유 여부 확인 (auth_roleAssignments 기반)
  // jobTitleLevel뿐 아니라 role_tl 역할도 TL로 인정
  const hasRoleTl = hasRole(authContext, ROLE_TL);

  // V114.4: TL 여부 통합 (직책 레벨 OR role_tl 역할)
  const isTl = isTlByJobTitle || hasRoleTl;

  // V60: MM 메뉴 노출 여부 (전체 접근 역할 OR TL)
  // - 전체 접근 역할: 현황 + 등록 모두 노출
  // - TL만: 등록만 노출 (하위 메뉴 필터링에서 처리)
  // V114.4: isTlByJobTitle → isTl로 변경 (role_tl 포함)
  const hasMmMenuAccess = hasMmFullAccess || isTl;

  // V27.1: Admin 메뉴 포함 여부 - userType === 'admin' 또는 관리자급 역할
  // V29.2: system_admin 하드코딩 제거 → ADMIN_LIKE_ROLES 사용
  const hasAdminLikeRole = ADMIN_LIKE_ROLES.some(roleId => user?.roles?.includes(roleId));
  const isAdmin = userType === 'admin' || hasAdminLikeRole;
  const fullMenuStructure = isAdmin
    ? [...menuStructure, ...adminMenuStructure]
    : menuStructure;

  // 메뉴 펼침/접기 토글
  const toggleMenu = (menuId: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  // 현재 경로가 해당 메뉴의 하위 경로인지 확인
  const isMenuActive = (menuItem: MenuItem): boolean => {
    if (menuItem.path) {
      return location.pathname === menuItem.path;
    }
    if (menuItem.subMenus) {
      return menuItem.subMenus.some(sub => location.pathname === sub.path);
    }
    return false;
  };

  // 현재 경로가 하위 메뉴 항목인지 확인
  const isSubMenuActive = (path: string): boolean => {
    return location.pathname === path;
  };

  /**
   * 권한 기반 메뉴 필터링
   *
   * 1. authContext가 로드되지 않았으면 빈 배열 반환 (로딩 중)
   * 2. 단일 메뉴: 해당 경로에 접근 가능한지 체크
   * 3. 하위 메뉴가 있는 경우: 하위 메뉴 중 하나라도 접근 가능하면 표시
   */

  const visibleMenus = !authContext.isLoaded ? [] : fullMenuStructure.filter(menuItem => {
    // V257: 파견직은 증명서 발급 메뉴 숨김
    if (menuItem.id === 'my-certificates' && authContext.currentEmploymentTypeCode === 'DISPATCH') {
      return false;
    }

    // V70 Tournament: 사용자 메뉴 '2026년 게임대회' 는 BE 설정 (ON + 대상자 매칭) 시만 노출
    // 관리자라는 이유만으로 자동 노출되지 않음 (사용자 지시 §1)
    if (menuItem.id === 'tournament' && !canAccessTournament) {
      return false;
    }

    // 단일 메뉴인 경우
    if (menuItem.path) {
      const hasAccess = canAccessRoute(authContext, menuItem.path);
      return hasAccess;
    }

    // 하위 메뉴가 있는 경우: 하위 메뉴 중 하나라도 접근 가능하면 표시
    // V136: RBAC 권한 + 직책 레벨 정책 모두 체크
    // V148.1: role_exec는 org-management에서 members/projects만 허용
    // V168: 승인관리 메뉴는 (RBAC OR isOrgApprover) 조건으로 노출
    // V60: org-management/mm 메뉴 역할 분리 (TL은 MM 등록만)
    if (menuItem.subMenus) {
      // V60: 조직관리 메뉴는 허용 역할이 없으면 전체 숨김
      // V70.27: 단, 인력 현황 접근 권한자(Admin / 설정 등록 사용자)는 '인력 현황' 하나만 보이도록 통과시킨다.
      //         (하위 메뉴 필터에서 workforce-overview 외 org-management 메뉴는 차단됨)
      if (menuItem.id === 'org-management' && !hasOrgManagementAccess && !canAccessWorkforceOverview) {
        return false;
      }

      // V60: MM관리 메뉴는 전체 접근 역할 OR TL이 아니면 숨김
      if (menuItem.id === 'mm' && !hasMmMenuAccess) {
        return false;
      }

      // V202: 내 인사기록 메뉴는 role_exec 전체 숨김
      if (menuItem.id === 'my-hr' && isRoleExec) {
        return false;
      }

      const visibleSubMenus = menuItem.subMenus.filter(subMenu => {
        // V70-S: 감사 대응 인사관리 메뉴 — 확정 역할 + KR 회사 범위만 (RBAC 범용 권한으로 판정하지 않음)
        if (HR_KR_AUDIT_PATHS.includes(subMenu.path)) {
          return canAccessHrKrAuditMenu;
        }
        // V70.27: 인력 현황 — 접근 판정 SSoT 는 서버 설정(workforceOverviewSettings)이다.
        //          조직관리 직책 레벨 정책/role_exec override 와 독립적으로 판정한다.
        if (subMenu.path === '/org-management/workforce-overview') {
          return canAccessWorkforceOverview;
        }
        // V70.27: 조직관리 접근 역할이 없는 사용자에게는 '인력 현황' 외 조직관리 메뉴를 노출하지 않는다.
        //          (기존에는 상위 메뉴 자체가 숨겨져 동일 효과였으므로 기존 동작 유지용 가드)
        if (!hasOrgManagementAccess && subMenu.path.startsWith('/org-management/')) {
          return false;
        }
        // V47.15: 테스트도구 메뉴는 system_admin 전용
        if (subMenu.path === '/admin/test-utilities') {
          return isSystemAdmin;
        }
        // V82.7 Phase 1: 화이트리스트 메뉴 system_admin 전용
        if (subMenu.path === '/admin/ip-whitelist') {
          return isSystemAdmin;
        }
        // V67: 알림 마스터 메뉴 system_admin 전용
        if (subMenu.path === '/admin/notifications') {
          return isSystemAdmin;
        }

        // V70 엔글 룰렛 — 추첨 플랫폼 system_admin 전용 (사용자 지시 "지정 진행자 계정만 접근")
        if (subMenu.path === ROULETTE_PATH) {
          return canHostRouletteMenu;
        }
        if (subMenu.path === '/tournament/roulette/demo' || subMenu.path === '/tournament/roulette-v2') {
          return isSystemAdmin;
        }

        // V169: 승인요청 관리 메뉴 - SSoT 규칙에 따른 승인자도 접근 가능
        if (subMenu.path === '/work/approvals' && isOrgApprover) {
          return true;
        }

        // V5.18.1: 관리자는 모든 org-management 메뉴 접근 가능 (직책 레벨 정책 건너뜀)
        // isAdmin = userType === 'admin' || ADMIN_LIKE_ROLES 중 하나 보유
        if (isAdmin && subMenu.path.startsWith('/org-management/')) {
          return true;
        }

        // V91.3: 태그 마스터 메뉴 제거로 인해 project-tags 권한 체크 제거

        // V148.1: role_exec에 대한 org-management 하위 메뉴 override
        // V91.3: project-tags 제거
        // V202: interviews 추가 차단
        if (isRoleExec && subMenu.path.startsWith('/org-management/')) {
          const allowedPaths = ['/org-management/members', '/org-management/projects'];
          const deniedPaths = ['/org-management/work-status', '/org-management/leave', '/org-management/interviews'];

          if (deniedPaths.includes(subMenu.path)) return false;
          if (allowedPaths.includes(subMenu.path)) return true;
        }

        // V60: MM 하위 메뉴 필터링 - TL은 등록만 허용
        // V114.4: isTlByJobTitle → isTl로 변경 (role_tl 포함)
        if (subMenu.path.startsWith('/mm/')) {
          // V236: mm-master는 exec+admin만 (role_manager 제외)
          if (subMenu.path === '/mm/master') {
            return isAdmin || hasRole(authContext, 'role_exec');
          }
          // 전체 접근 역할이면 현황/등록 노출
          if (hasMmFullAccess) return true;
          // TL인 경우 등록만 노출 (현황/마스터 숨김)
          if (isTl && subMenu.path === '/mm/register') return true;
          // 그 외는 숨김
          return false;
        }

        // 1. RBAC 권한 체크
        if (!canAccessRoute(authContext, subMenu.path)) return false;
        // 2. V136: 직책 레벨 정책 체크 (조직관리 메뉴만)
        if (!canAccessByPolicy(subMenu.path)) return false;
        return true;
      });
      return visibleSubMenus.length > 0;
    }

    return true;
  });

  /**
   * 특정 메뉴의 접근 가능한 하위 메뉴만 필터링
   * V136: RBAC 권한 + 직책 레벨 정책 모두 체크
   * V148.1: role_exec는 org-management에서 members/projects만 허용, work-status/leave 숨김
   * V168: 승인관리 메뉴는 (RBAC OR isOrgApprover) 조건으로 노출
   * V60: MM 하위 메뉴 - TL은 등록만 허용
   * V114.4: TL 판별에 role_tl 역할 포함
   */
  const getVisibleSubMenus = (subMenus: SubMenuItem[]): SubMenuItem[] => {
    return subMenus.filter(subMenu => {
      // V70-S: 감사 대응 인사관리 메뉴 — 확정 역할 + KR 회사 범위만 (visibleMenus 와 동일 기준)
      if (HR_KR_AUDIT_PATHS.includes(subMenu.path)) {
        return canAccessHrKrAuditMenu;
      }
      // V70.27: 인력 현황 — 접근 판정 SSoT 는 서버 설정(workforceOverviewSettings)
      if (subMenu.path === '/org-management/workforce-overview') {
        return canAccessWorkforceOverview;
      }
      // V70.27: 조직관리 접근 역할이 없으면 '인력 현황' 외 조직관리 메뉴 비노출 (기존 동작 유지)
      if (!hasOrgManagementAccess && subMenu.path.startsWith('/org-management/')) {
        return false;
      }
      // V47.15: 테스트도구 메뉴는 system_admin 전용
      if (subMenu.path === '/admin/test-utilities') {
        return isSystemAdmin;
      }

      // V70G8G: 엔글 룰렛 — 실제 렌더 목록에도 진행자 조건 적용 (누락되어 게임대회 접근자 전원에게 노출됐음)
      if (subMenu.path === ROULETTE_PATH) {
        return canHostRouletteMenu;
      }
      // V70 엔글 룰렛 — V1/V2 메뉴 system_admin 전용 (사용자 지시 "지정 진행자 계정만 접근")
      if (subMenu.path === '/tournament/roulette/demo' || subMenu.path === '/tournament/roulette-v2') {
        return isSystemAdmin;
      }

      // V169: 승인요청 관리 메뉴 - SSoT 규칙에 따른 승인자도 접근 가능
      if (subMenu.path === '/work/approvals' && isOrgApprover) {
        return true;
      }

      // V5.18.1: 관리자는 모든 org-management 메뉴 접근 가능 (직책 레벨 정책 건너뜀)
      // isAdmin = userType === 'admin' || ADMIN_LIKE_ROLES 중 하나 보유
      if (isAdmin && subMenu.path.startsWith('/org-management/')) {
        return true;
      }

      // V91.3: 태그 마스터 메뉴 제거로 인해 project-tags 권한 체크 제거

      // V148.1: role_exec에 대한 org-management 하위 메뉴 override
      // V91.3: project-tags 제거
      // V202: interviews 추가 차단
      if (isRoleExec && subMenu.path.startsWith('/org-management/')) {
        // role_exec: members, projects만 허용
        const allowedPaths = ['/org-management/members', '/org-management/projects'];
        // role_exec: work-status, leave, interviews 차단
        const deniedPaths = ['/org-management/work-status', '/org-management/leave', '/org-management/interviews'];

        if (deniedPaths.includes(subMenu.path)) return false;
        if (allowedPaths.includes(subMenu.path)) return true;
        // 조직원면담은 기존 정책대로 (직책 레벨 정책 체크)
      }

      // V60: MM 하위 메뉴 필터링 - TL은 등록만 허용
      // V114.4: isTlByJobTitle → isTl로 변경 (role_tl 포함)
      if (subMenu.path.startsWith('/mm/')) {
        // V236: mm-master는 exec+admin만 (role_manager 제외)
        if (subMenu.path === '/mm/master') {
          return isAdmin || hasRole(authContext, 'role_exec');
        }
        // 전체 접근 역할이면 현황/등록 노출
        if (hasMmFullAccess) return true;
        // TL인 경우 등록만 노출 (현황/마스터 숨김)
        if (isTl && subMenu.path === '/mm/register') return true;
        // 그 외는 숨김
        return false;
      }

      // 1. RBAC 권한 체크
      if (!canAccessRoute(authContext, subMenu.path)) return false;
      // 2. V136: 직책 레벨 정책 체크 (조직관리 메뉴만)
      if (!canAccessByPolicy(subMenu.path)) return false;
      return true;
    });
  };

  return (
    <div style={{
      width: '280px',
      background: 'white',
      boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
      position: 'fixed',
      height: '100vh',
      zIndex: 100,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* 로고 */}
      <div style={{
        padding: '12px 24px',
        borderBottom: '1px solid var(--color-gray-200)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        <img
          src="/ngle.png"
          alt="nGle Logo"
          style={{ width: '48px', height: '48px', objectFit: 'contain' }}
        />
        <div>
          <div style={{ fontWeight: 'bold', color: 'var(--color-gray-800)', fontSize: '20px' }}>
            Works
          </div>
          <div style={{ fontSize: '11px', color: 'var(--color-gray-500)' }}>
            ERP System v1.0
          </div>
        </div>
      </div>

      {/* 사용자 정보 */}
      <div style={{
        padding: '12px 16px 12px 32px',
        borderBottom: '1px solid var(--color-gray-200)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <div style={{
          width: '36px',
          height: '36px',
          background: 'linear-gradient(135deg, #cffafe, #a8d8dd)',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
          fontWeight: 'bold',
          color: 'var(--color-primary)',
        }}>
          {(user?.profile as any)?.nameKo?.charAt(0) || user?.profile?.displayName?.charAt(0) || 'U'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: '600',
            color: 'var(--color-gray-800)',
            fontSize: '15px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {(user?.profile as any)?.nameKo || user?.profile?.displayName || '-'}
          </div>
          <div style={{
            fontSize: '11px',
            color: 'var(--color-gray-500)',
            fontWeight: '600',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {(user?.profile as any)?.orgUnitName || ''}{' '}
            {(user?.profile as any)?.jobTitle === '팀원'
              ? (user?.profile as any)?.positionTitle
              : (user?.profile as any)?.jobTitle || ''}
          </div>
        </div>
      </div>

      {/* 메뉴 */}
      <nav style={{ padding: '12px', flex: 1, overflowY: 'auto' }}>
        {/* 로딩 중 */}
        {!authContext.isLoaded && (
          <div style={{
            padding: '24px',
            textAlign: 'center',
            color: 'var(--color-gray-500)',
            fontSize: '14px'
          }}>
            메뉴 로딩 중...
          </div>
        )}

        {/* 메뉴 목록 */}
        {visibleMenus.map(menuItem => {
          const Icon = menuItem.icon;
          const active = isMenuActive(menuItem);
          const expanded = expandedMenus.includes(menuItem.id);
          const hasSubMenus = menuItem.subMenus && menuItem.subMenus.length > 0;

          // 하위 메뉴가 없는 경우 (단일 메뉴)
          if (!hasSubMenus && menuItem.path) {
            return (
              <Link
                key={menuItem.id}
                to={menuItem.path}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  marginBottom: '4px',
                  background: active ? 'var(--color-primary-lighter)' : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  color: active ? 'var(--color-primary)' : 'var(--color-gray-600)',
                  fontWeight: active ? '600' : '500',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = 'var(--color-gray-50)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <Icon size={20} />
                <span style={{ flex: 1 }}>{menuItem.label}</span>
                {/* V84: 공지사항 새 글 배지 */}
                {menuItem.id === 'announcements' && unreadAnnouncementCount > 0 && (
                  <span
                    style={{
                      minWidth: '18px',
                      height: '18px',
                      padding: '0 5px',
                      background: '#ef4444',
                      color: 'white',
                      borderRadius: '9px',
                      fontSize: '11px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {unreadAnnouncementCount > 9 ? '9+' : unreadAnnouncementCount}
                  </span>
                )}
              </Link>
            );
          }

          // 하위 메뉴가 있는 경우
          return (
            <div key={menuItem.id} style={{ marginBottom: '4px' }}>
              {/* 상위 메뉴 */}
              <button
                onClick={() => toggleMenu(menuItem.id)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: active ? 'var(--color-primary-lighter)' : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  color: active ? 'var(--color-primary)' : 'var(--color-gray-600)',
                  fontWeight: active ? '600' : '500',
                  fontSize: '14px',
                  transition: 'all 0.2s ease',
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = 'var(--color-gray-50)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    e.currentTarget.style.background = active ? 'var(--color-primary-lighter)' : 'transparent';
                  }
                }}
              >
                <Icon size={20} />
                <span style={{ flex: 1, textAlign: 'left' }}>{menuItem.label}</span>
                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>

              {/* 하위 메뉴 */}
              {expanded && menuItem.subMenus && (
                <div style={{ marginLeft: '20px', marginTop: '4px' }}>
                  {getVisibleSubMenus(menuItem.subMenus).map(subMenu => {
                    const subActive = isSubMenuActive(subMenu.path);
                    return (
                      <Link
                        key={subMenu.id}
                        to={subMenu.path}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          marginBottom: '2px',
                          background: subActive ? 'var(--color-primary-lighter)' : 'transparent',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          color: subActive ? 'var(--color-primary)' : 'var(--color-gray-600)',
                          fontWeight: subActive ? '600' : '400',
                          fontSize: '13px',
                          transition: 'all 0.2s ease',
                          textDecoration: 'none'
                        }}
                        onMouseEnter={(e) => {
                          if (!subActive) {
                            e.currentTarget.style.background = 'var(--color-gray-50)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!subActive) {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }}
                      >
                        <span>{subMenu.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* V62: 내정보 버튼 (사이드바 최하단) */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid var(--color-gray-200)',
        marginTop: 'auto',
      }}>
        <Link
          to="/my/profile"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 16px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: location.pathname === '/my/profile' ? '600' : '500',
            color: location.pathname === '/my/profile' ? 'var(--color-primary)' : 'var(--color-gray-600)',
            background: location.pathname === '/my/profile' ? 'var(--color-primary-light, #e8f4ff)' : 'transparent',
            transition: 'background 0.15s ease',
          }}
        >
          <User size={18} />
          <span>내정보</span>
        </Link>
      </div>
    </div>
  );
}

/**
 * ============================================
 * TODO: 권한 기반 메뉴 노출 제어 (향후 구현)
 * ============================================
 *
 * 현재는 모든 메뉴가 모든 사용자에게 표시됩니다.
 * 향후 RBAC 시스템을 연동하여 권한에 따라 메뉴를 숨기거나 표시할 수 있습니다.
 *
 * **구현 방법:**
 *
 * 1. AuthContext를 가져오기:
 *
 * ```tsx
 * import { useAuthContext } from '../../hooks/useAuthContext';
 * import { canAccessRoute } from '../../utils/rbac';
 *
 * export function Sidebar() {
 *   const authContext = useAuthContext();
 *   // ... 기존 코드
 * }
 * ```
 *
 * 2. 메뉴 아이템 필터링:
 *
 * ```tsx
 * // 메뉴 구조에서 권한이 있는 메뉴만 필터링
 * const visibleMenus = menuStructure.filter(menuItem => {
 *   // 단일 메뉴인 경우
 *   if (menuItem.path) {
 *     return canAccessRoute(authContext, menuItem.path);
 *   }
 *
 *   // 하위 메뉴가 있는 경우: 하위 메뉴 중 하나라도 접근 가능하면 표시
 *   if (menuItem.subMenus) {
 *     const visibleSubMenus = menuItem.subMenus.filter(subMenu =>
 *       canAccessRoute(authContext, subMenu.path)
 *     );
 *     return visibleSubMenus.length > 0;
 *   }
 *
 *   return true;
 * });
 * ```
 *
 * 3. 하위 메뉴도 필터링:
 *
 * ```tsx
 * {visibleMenus.map(menuItem => {
 *   // ... 기존 코드
 *
 *   // 하위 메뉴 필터링
 *   if (expanded && menuItem.subMenus) {
 *     const visibleSubMenus = menuItem.subMenus.filter(subMenu =>
 *       canAccessRoute(authContext, subMenu.path)
 *     );
 *
 *     return (
 *       <div style={{ marginLeft: '20px', marginTop: '4px' }}>
 *         {visibleSubMenus.map(subMenu => {
 *           // ... 기존 하위 메뉴 렌더링 코드
 *         })}
 *       </div>
 *     );
 *   }
 * })}
 * ```
 *
 * **예시: 특정 메뉴별 권한 제어**
 *
 * - **인사관리 (hr)**: hr 또는 super_admin만 접근 가능
 *   - canAccessRoute(ctx, '/hr/employees') → hr.manage 권한 필요
 *
 * - **MM관리 (mm)**: tl, org_manager, hr, executive, super_admin 접근 가능
 *   - MM 현황: mm.read 권한 필요
 *   - MM 등록: mm.register 권한 필요
 *
 * - **팀관리 (team)**: org_manager, hr, super_admin 접근 가능
 *   - team.my.manage 권한 필요
 *
 * - **승인관리 (work/approvals)**: org_manager, hr, approver, super_admin 접근 가능
 *   - work.approve 권한 필요
 *
 * - **마스터데이터 (master)**: 모든 사용자 조회 가능, hr/super_admin만 수정 가능
 *   - 메뉴는 모두 표시하되, 페이지 내에서 수정 버튼만 권한 체크
 *
 * **DEMO 모드 테스트:**
 *
 * ```tsx
 * // DEMO 모드에서 특정 사용자로 테스트
 * import { DEMO_AUTH_CONTEXTS } from '../../utils/demoAuthContexts';
 *
 * // 일반 직원으로 테스트
 * const authContext = DEMO_AUTH_CONTEXTS.general;
 * // → 인사관리, MM관리, 팀관리 메뉴가 숨겨짐
 *
 * // HR로 테스트
 * const authContext = DEMO_AUTH_CONTEXTS.hr;
 * // → 모든 관리 메뉴가 표시됨
 * ```
 */

/**
 * ============================================
 * Sidebar와 Route Guard의 관계 (이중 방어 구조)
 * ============================================
 *
 * **접근 제어 이중 방어 시스템:**
 *
 * nGle-Works ERP는 권한 없는 사용자의 접근을 막기 위해
 * 2단계 방어 시스템을 운영합니다.
 *
 * **1단계: Sidebar 메뉴 필터링 (UX 개선)**
 * - 역할: 권한 없는 메뉴를 "보이지 않게" 처리
 * - 구현: 이 Sidebar 컴포넌트에서 canAccessRoute() 사용
 * - 효과:
 *   - 사용자는 자신이 접근할 수 있는 메뉴만 볼 수 있음
 *   - 혼란 방지: "왜 저 메뉴는 안 눌리지?" 같은 혼란 방지
 *   - UX 향상: 깔끔한 메뉴 구조
 *
 * **2단계: Route Guard (실제 보안)**
 * - 역할: 라우트 접근 권한을 "실제로 차단"
 * - 구현: App.tsx의 ProtectedRoute 컴포넌트
 * - 효과:
 *   - URL 직접 입력으로 우회 시도 차단
 *   - 브라우저 주소창에 /hr/employees 입력해도 권한 없으면 403
 *   - ForbiddenPage 표시
 *
 * **왜 두 가지 모두 필요한가?**
 *
 * - Sidebar만 있으면:
 *   - 메뉴는 숨겨지지만, URL 직접 입력하면 접근 가능 (보안 취약)
 *
 * - Route Guard만 있으면:
 *   - 보안은 지켜지지만, 사용자가 권한 없는 메뉴를 클릭해야 "접근 거부" 확인 (UX 나쁨)
 *
 * - 둘 다 있으면:
 *   - Sidebar: 권한 없는 메뉴는 아예 안 보임 (UX 좋음)
 *   - Route Guard: URL 우회 시도도 차단 (보안 좋음)
 *   - ✅ 최고의 보안 + 최고의 UX
 *
 * **동일한 RBAC 정책 사용:**
 *
 * - Sidebar와 ProtectedRoute 모두 `canAccessRoute()`를 사용
 * - 권한 정책이 한 곳(rbac.ts)에 집중 관리됨
 * - 일관성 있는 권한 체크
 *
 * **예시 흐름:**
 *
 * 1. 일반 직원(general)이 로그인
 * 2. Sidebar: "직원관리" 메뉴가 보이지 않음 (1단계 차단)
 * 3. 사용자가 브라우저 주소창에 /hr/employees 직접 입력
 * 4. ProtectedRoute: canAccessRoute() → false
 * 5. ForbiddenPage 표시 (2단계 차단)
 *
 * **PM 참고:**
 *
 * - 사용자에게는 권한 없는 메뉴가 안 보이므로 혼란이 없습니다.
 * - 개발자가 URL을 직접 입력해도 권한이 체크되므로 안전합니다.
 * - Sidebar와 Route Guard는 같은 RBAC 정책을 공유하여 일관성이 보장됩니다.
 */
